const REVIEW_TYPES = new Set(["customers", "merchants"]);
const REVIEW_STATUSES = new Set([
  "PENDING_REVIEW",
  "REUPLOAD_REQUIRED",
  "REJECTED"
]);
const REVIEW_SORTS = new Set(["registeredAt:asc", "registeredAt:desc"]);
const DATE_PRESETS = new Set([
  "all",
  "today",
  "last7",
  "last30",
  "last90",
  "custom"
]);
const QUERY_KEYS = [
  "type",
  "page",
  "perPage",
  "search",
  "reviewStatus",
  "datePreset",
  "registeredFrom",
  "registeredTo",
  "sort"
];

const positiveInteger = (value, fallback, maximum) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return maximum ? Math.min(parsed, maximum) : parsed;
};

const calendarDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
    ? match[0]
    : "";
};

const toSearchParams = (source) => {
  if (source instanceof URLSearchParams) return source;
  const params = new URLSearchParams();
  Object.entries(source || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) params.set(key, String(value));
  });
  return params;
};

export const normalizeRegistrationReviewFilters = (source) => {
  const params = toSearchParams(source);
  const type = params.get("type");
  const reviewStatus = params.get("reviewStatus");
  const sort = params.get("sort");
  const from = calendarDate(params.get("registeredFrom"));
  const to = calendarDate(params.get("registeredTo"));
  const requestedPreset = params.get("datePreset");
  const datePreset = DATE_PRESETS.has(requestedPreset)
    ? requestedPreset
    : from || to
      ? "custom"
      : "all";
  return {
    type: REVIEW_TYPES.has(type) ? type : "customers",
    page: positiveInteger(params.get("page"), 1),
    perPage: positiveInteger(params.get("perPage"), 10, 100),
    search: String(params.get("search") || "").trim(),
    reviewStatus: REVIEW_STATUSES.has(reviewStatus) ? reviewStatus : "",
    datePreset,
    registeredFrom: datePreset === "custom" ? from : "",
    registeredTo: datePreset === "custom" ? to : "",
    sort: REVIEW_SORTS.has(sort) ? sort : "registeredAt:desc"
  };
};

const formatCalendarDate = (date) => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const cairoCalendarDate = (date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Africa/Cairo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(date);
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
};

export const resolveRegistrationDateRange = (source, now = new Date()) => {
  const filters = normalizeRegistrationReviewFilters(source);
  if (filters.datePreset === "custom") {
    return {
      registeredFrom: filters.registeredFrom,
      registeredTo: filters.registeredTo
    };
  }
  const days = {
    today: 1,
    last7: 7,
    last30: 30,
    last90: 90
  }[filters.datePreset];
  if (!days) return { registeredFrom: "", registeredTo: "" };
  const endValue = cairoCalendarDate(now);
  const [year, month, day] = endValue.split("-").map(Number);
  const start = new Date(Date.UTC(year, month - 1, day - (days - 1)));
  return {
    registeredFrom: formatCalendarDate(start),
    registeredTo: endValue
  };
};

export const buildRegistrationReviewApiQuery = (source, now = new Date()) => {
  const filters = normalizeRegistrationReviewFilters(source);
  const range = resolveRegistrationDateRange(filters, now);
  const hasRange = Boolean(range.registeredFrom || range.registeredTo);
  const params = buildRegistrationReviewQuery({
    ...filters,
    ...range,
    datePreset: hasRange ? "custom" : "all"
  });
  params.delete("datePreset");
  return params;
};

export const buildRegistrationReviewQuery = (source = {}) => {
  const filters = normalizeRegistrationReviewFilters(source);
  const params = new URLSearchParams();
  QUERY_KEYS.forEach((key) => {
    const value = filters[key];
    if (value !== "" && value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  });
  return params;
};

export {
  DATE_PRESETS,
  QUERY_KEYS,
  REVIEW_SORTS,
  REVIEW_STATUSES,
  REVIEW_TYPES
};
